import { useMemo, useState, type CSSProperties, type ComponentType } from 'react'
import { Banknote, Bot, Boxes, Building2, Cable, Cpu, Droplets, ExternalLink, Factory, Gauge, Network, ShieldCheck, Smartphone, Users, Zap } from 'lucide-react'
import { calculatePowerComparisons, infrastructureSources, POWER_ASSUMPTIONS } from '../../data/infrastructureExplainer'

type Stage = {
  id: string
  number: string
  label: string
  headline: string
  explanation: string
  dependsOn: string[]
  constraint: string
  Icon: ComponentType<{ 'aria-hidden'?: boolean }>
}

const stages: Stage[] = [
  {
    id: 'demand',
    number: '01',
    label: 'AI demand',
    headline: 'A useful AI service begins by asking machines to do work.',
    explanation: 'Training and inference turn software demand into repeated calculations. More users, larger models, and richer outputs increase the amount of physical computing required.',
    dependsOn: ['Compute capacity', 'Capital', 'Networks'],
    constraint: 'A model can be ready before the chips, buildings, and electricity needed to serve it are available.',
    Icon: Bot,
  },
  {
    id: 'compute',
    number: '02',
    label: 'Compute',
    headline: 'Chips, servers, memory, and networks perform the calculations.',
    explanation: 'AI accelerators are assembled into servers and racks, linked by high-speed networks, and operated as one large computing system.',
    dependsOn: ['Semiconductors', 'Servers and racks', 'Cooling'],
    constraint: 'A rack is not useful without power delivery, heat removal, networking, maintenance, and replacement equipment.',
    Icon: Cpu,
  },
  {
    id: 'datacenters',
    number: '03',
    label: 'Data centers',
    headline: 'The computer becomes a building—and often an entire campus.',
    explanation: 'Data centers combine computing rooms with substations, backup systems, cooling plants, fiber connections, security, and operations teams.',
    dependsOn: ['Land and buildings', 'Electricity', 'Water or air cooling'],
    constraint: 'The campus must be designed around both the IT load and the extra power used to cool and support it.',
    Icon: Building2,
  },
  {
    id: 'grid',
    number: '04',
    label: 'Grid',
    headline: 'Electricity has to reach the site at the right voltage, every second.',
    explanation: 'Transmission lines move bulk power. Substations, transformers, and switchgear step it down and route it into the data-center electrical system.',
    dependsOn: ['Transmission capacity', 'Substations', 'Interconnection approval'],
    constraint: 'A region may have enough annual electricity yet still lack a nearby line, transformer, or interconnection slot for a new large load.',
    Icon: Network,
  },
  {
    id: 'power',
    number: '05',
    label: 'Power',
    headline: 'Generators and storage keep the electrical system supplied.',
    explanation: 'Natural gas, coal, nuclear, hydro, wind, solar, storage, and other resources contribute different combinations of energy, capacity, flexibility, and reliability.',
    dependsOn: ['Generation capacity', 'Fuel or weather', 'Grid balancing'],
    constraint: 'Nameplate capacity is not the same as electricity produced over a year; output depends on availability and capacity factor.',
    Icon: Zap,
  },
  {
    id: 'inputs',
    number: '06',
    label: 'Physical inputs',
    headline: 'Every layer rests on supply chains, people, land, and permission.',
    explanation: 'Fuel, water, transformers, construction equipment, fiber, skilled workers, financing, permits, and community decisions determine what can actually be delivered.',
    dependsOn: ['Equipment and fuel', 'Workforce and capital', 'Permits and communities'],
    constraint: 'The tightest constraint may sit far upstream from the AI company whose demand started the project.',
    Icon: Boxes,
  },
]

const supportInputs = [
  { label: 'Cooling + water', Icon: Droplets },
  { label: 'Equipment + fuel', Icon: Factory },
  { label: 'Capital', Icon: Banknote },
  { label: 'Permits + people', Icon: Users },
  { label: 'Fiber + networks', Icon: Cable },
  { label: 'Reliability', Icon: ShieldCheck },
]

const powerScale = [
  { label: 'Phone charging', valueMw: 0.000015, display: '15 W', note: 'Illustrative charging draw' },
  { label: 'Average U.S. home', valueMw: (POWER_ASSUMPTIONS.usHomeMonthlyKwh * 12 / 8_760) / 1_000, display: '1.18 kW', note: 'Average demand from 2024 use' },
  { label: 'Conventional data center', valueMw: 25, display: '25 MW', note: 'IEA comparison point' },
  { label: 'Hyperscale AI data center', valueMw: 100, display: '100+ MW', note: 'IEA comparison point' },
  { label: 'Coal unit example', valueMw: 500, display: '500 MW', note: 'Nameplate, before capacity factor' },
  { label: 'Largest under construction', valueMw: 2_000, display: '≈2 GW', note: 'IEA 2024 comparison point' },
]

const presets = [25, 100, 500, 2_000]
const compactNumber = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 })
const wholeNumber = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })

function scaleWidth(valueMw: number): number {
  const min = Math.log10(powerScale[0].valueMw)
  const max = Math.log10(powerScale[powerScale.length - 1].valueMw)
  return 4 + ((Math.log10(valueMw) - min) / (max - min)) * 96
}

function energyLabel(mwh: number): string {
  if (mwh >= 1_000) return `${compactNumber.format(mwh / 1_000)} GWh`
  return `${wholeNumber.format(mwh)} MWh`
}

export function InfrastructureExplainer() {
  const [selectedStageId, setSelectedStageId] = useState(stages[0].id)
  const [loadMw, setLoadMw] = useState(100)
  const selectedStage = stages.find((stage) => stage.id === selectedStageId) ?? stages[0]
  const comparisons = useMemo(() => calculatePowerComparisons(loadMw), [loadMw])
  const coalShare = comparisons.coalAverageOutputShare

  return <>
    <section className="infra-explainer" aria-labelledby="infra-explainer-title">
      <header className="infra-explainer-heading">
        <div><span className="eyebrow">ONE SYSTEM · SIX DEPENDENCIES</span><h2 id="infra-explainer-title">Follow an AI request into the physical world.</h2></div>
        <p>Select each layer. The arrows point toward what the system needs next—not toward what is newest or most visible.</p>
      </header>

      <div className="infra-chain" aria-label="AI infrastructure dependency chain">
        {stages.map((stage) => <button type="button" className={stage.id === selectedStage.id ? 'is-active' : ''} aria-pressed={stage.id === selectedStage.id} onClick={() => setSelectedStageId(stage.id)} key={stage.id}>
          <span>{stage.number}</span><stage.Icon aria-hidden/><strong>{stage.label}</strong>
        </button>)}
      </div>

      <article className="infra-stage-detail" aria-live="polite">
        <div className="infra-stage-identity"><selectedStage.Icon aria-hidden/><span>{selectedStage.number} / {selectedStage.label.toUpperCase()}</span><strong>{selectedStage.headline}</strong></div>
        <div className="infra-stage-copy"><p>{selectedStage.explanation}</p><dl><div><dt>WHAT THIS NEEDS</dt><dd>{selectedStage.dependsOn.join(' · ')}</dd></div><div><dt>WHERE DELIVERY GETS HARD</dt><dd>{selectedStage.constraint}</dd></div></dl></div>
      </article>

      <div className="infra-support-band" aria-label="Inputs that support every infrastructure layer">
        <span>RUNNING THROUGH THE WHOLE CHAIN</span>
        <div>{supportInputs.map(({ label, Icon }) => <div key={label}><Icon aria-hidden/><strong>{label}</strong></div>)}</div>
      </div>
    </section>

    <section className="power-explainer" aria-labelledby="power-explainer-title">
      <header className="power-explainer-heading">
        <div><span className="eyebrow">POWER · A REAL-WORLD SCALE</span><h2 id="power-explainer-title">What does a megawatt actually mean?</h2></div>
        <p>Power is a rate at one moment. Energy is that rate sustained over time. Keeping those two ideas separate makes the comparisons honest.</p>
      </header>

      <div className="power-unit-primer">
        <article><Gauge aria-hidden="true"/><span>POWER · RIGHT NOW</span><strong>1 MW = 1,000 kW = 1,000,000 W</strong><p>A megawatt describes how fast electricity is being produced or used at an instant.</p></article>
        <article><Zap aria-hidden="true"/><span>ENERGY · OVER TIME</span><strong>1 MW for 1 hour = 1 MWh</strong><p>A 100 MW data center running for a full day uses 2,400 MWh, or 2.4 GWh.</p></article>
      </div>

      <div className="power-scale-layout">
        <section className="power-scale-chart" aria-labelledby="power-scale-heading">
          <div><span>LOGARITHMIC COMPARISON</span><h3 id="power-scale-heading">From one phone to a multi-gigawatt campus</h3><p>Each move to the right represents a large multiplication in power—not an equal number of megawatts.</p></div>
          <ol>{powerScale.map((item) => <li key={item.label}>
            <div><strong>{item.label}</strong><span>{item.note}</span></div><div className="power-scale-track" aria-hidden="true"><i style={{ '--power-width': `${scaleWidth(item.valueMw)}%` } as CSSProperties}/></div><b>{item.display}</b>
          </li>)}</ol>
        </section>

        <section className="power-calculator" aria-labelledby="power-calculator-heading">
          <div className="power-calculator-head"><span>SIZE A DATA CENTER</span><h3 id="power-calculator-heading">If the site draws <output htmlFor="power-load">{wholeNumber.format(loadMw)} MW</output> continuously…</h3></div>
          <label htmlFor="power-load">Data-center power demand</label>
          <input id="power-load" type="range" min="1" max="2000" step="1" value={loadMw} onChange={(event) => setLoadMw(Number(event.target.value))}/>
          <div className="power-presets" aria-label="Data-center size presets">{presets.map((value) => <button type="button" className={loadMw === value ? 'is-active' : ''} aria-pressed={loadMw === value} onClick={() => setLoadMw(value)} key={value}>{value >= 1_000 ? `${value / 1_000} GW` : `${value} MW`}</button>)}</div>
          <div className="power-results" aria-live="polite">
            <article><strong>{energyLabel(comparisons.dailyEnergyMwh)}</strong><span>electricity each day</span></article>
            <article><strong>{compactNumber.format(comparisons.averageHouseholdsEquivalent)}</strong><span>average U.S. homes at the same continuous demand</span></article>
            <article><strong>{compactNumber.format(comparisons.phoneFullChargesPerHour)}</strong><span>illustrative 15 Wh phone charges per hour</span></article>
          </div>
          <p className="power-calculator-note">This load would equal {coalShare < 1 ? <><strong>{wholeNumber.format(coalShare * 100)}%</strong> of</> : <><strong>{coalShare.toFixed(1)}×</strong></>} the average 2024 output of the 500 MW coal-unit example below. It is an equivalence—not a claim that the site is powered by coal.</p>
        </section>
      </div>

      <article className="coal-comparison">
        <div><Factory aria-hidden="true"/><span>500 MW COAL UNIT · ILLUSTRATIVE SIZE</span><strong>500 MW nameplate × 42.6% capacity factor = 213 MW average output</strong></div>
        <div><span><b>{compactNumber.format(comparisons.coalAnnualEnergyMwh)}</b> annual MWh</span><span><b>{compactNumber.format(comparisons.coalAnnualHouseholdsEquivalent)}</b> average U.S. homes for one year</span></div>
        <p>A plant’s nameplate is its maximum rated output. The annual comparison uses the EIA’s 2024 capacity factor for the U.S. utility-scale coal fleet, so it does not imply that every coal plant operates the same way.</p>
      </article>

      <details className="power-sources">
        <summary>Sources, assumptions, and calculation notes</summary>
        <div>{infrastructureSources.map((source) => <a href={source.url} target="_blank" rel="noreferrer" key={source.label}><span><strong>{source.label}</strong><small>{source.publisher}</small></span><p>{source.note}</p><ExternalLink aria-hidden="true"/></a>)}</div>
        <p><Smartphone aria-hidden="true"/> The phone comparison is deliberately labeled as an illustration: a 15 W charging draw and a 15 Wh full charge. Actual phones and charging losses vary. Household and plant comparisons are mathematical equivalents, not statements about a particular grid connection or generation source.</p>
      </details>
    </section>
  </>
}
